import React, { useState, useEffect } from 'react';
import './Game.css';
import api from '../utils/api';
import { STORAGE_KEYS } from '../constants';

interface GuessThePersonProps {
  roomId: string;
  roomData: any;
}

const QUESTIONS = [
  'The hallmark of a good relationship is?',
  'Unusual Skill?',
  'A life goal of mine?',
  'Typical Sunday?',
  'Dating me is like?',
  'You should not go out with me if?',
  "I'm convinced that?",
  'I want someone who?',
  'This year, I really want to?',
  'A quick rant about?',
  'I can teach you to?',
  'Two truth and a Lie?',
  'I go crazy for?',
  '3 words to describe me?'
];

function GuessThePerson({ roomId, roomData }: GuessThePersonProps) {
  const [stage, setStage] = useState<'preparation' | 'game' | 'final'>('preparation');
  const [selectedQuestions, setSelectedQuestions] = useState<string[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState({ totalParticipants: 0, submittedCount: 0, participants: [] });
  const [allAnswers, setAllAnswers] = useState<any[]>([]);
  const [currentPersonIndex, setCurrentPersonIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const [incorrectCount, setIncorrectCount] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    if (userData) {
      const user = JSON.parse(userData);
      const userId = user.userId || user.user_id;
      const adminUserId = roomData?.adminUserId || roomData?.admin_user_id;
      console.log('[GuessThePerson] Admin check:', { userId, adminUserId, isAdmin: userId === adminUserId });
      setIsAdmin(userId === adminUserId);
    }
  }, [roomData]);

  useEffect(() => {
    if (roomData?.gameState === 'preparation') {
      setStage('preparation');
    } else if (roomData?.gameState === 'ongoing') {
      setStage('game');
      if (selectedQuestions.length === 0) {
        const shuffled = [...QUESTIONS].sort(() => Math.random() - 0.5);
        setSelectedQuestions(shuffled.slice(0, 4));
      }
    } else if (roomData?.gameState === 'ended') {
      setStage('final');
      fetchAllAnswers();
    }
  }, [roomData?.gameState]);

  useEffect(() => {
    if (stage === 'game') {
      const interval = setInterval(fetchSubmissionStatus, 3000);
      return () => clearInterval(interval);
    }
  }, [stage]);

  const fetchSubmissionStatus = async () => {
    const result = await api.getSubmissionStatus(roomId);
    if (result.success) {
      setSubmissionStatus(result.data);
    }
  };

  const fetchAllAnswers = async () => {
    const result = await api.getAllAnswers(roomId);
    if (result.success) {
      setAllAnswers(result.data.answers);
    }
  };

  const handleAnswerChange = (question: string, value: string) => {
    setAnswers({ ...answers, [question]: value });
  };

  const handleSubmit = async () => {
    const allFilled = selectedQuestions.every(q => answers[q]?.trim());
    if (!allFilled) {
      alert('Please answer all questions');
      return;
    }

    const result = await api.submitAnswers(roomId, answers);
    if (result.success) {
      setSubmitted(true);
      fetchSubmissionStatus();
    } else {
      alert(result.error);
    }
  };

  const handleEndGame = async () => {
    const result = await api.endGame(roomId);
    if (result.success) {
      setStage('final');
      fetchAllAnswers();
    } else {
      alert(result.error);
    }
  };

  const handleNextPerson = () => {
    if (currentPersonIndex < allAnswers.length - 1) {
      setCurrentPersonIndex(currentPersonIndex + 1);
      setRevealed(false);
    }
  };

  const handleReveal = () => {
    setRevealed(true);
  };

  const handleGuessResult = (correct: boolean) => {
    if (correct) {
      setCorrectCount(correctCount + 1);
    } else {
      setIncorrectCount(incorrectCount + 1);
    }
  };

  if (stage === 'preparation') {
    return (
      <div className="game-container">
        <div className="game-header">
          <h2>🤔 Guess The Person</h2>
          <div className="room-id-display">{roomId}</div>
          <div className="game-info">
            <div className="info-item">
              <div className="info-label">Room Name</div>
              <div className="info-value">{roomData?.roomName}</div>
            </div>
            <div className="info-item">
              <div className="info-label">Players</div>
              <div className="info-value">{roomData?.currentParticipantCount}/{roomData?.maxParticipants}</div>
            </div>
          </div>
        </div>
        <div className="game-content">
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p style={{ fontSize: '1.2rem', marginBottom: '2rem' }}>⏳ Waiting for admin to start the game...</p>
          </div>
        </div>
      </div>
    );
  }

  if (stage === 'game') {
    return (
      <div className="game-container">
        <div className="game-header">
          <h2>🤔 Guess The Person</h2>
          <div className="room-id-display">{roomId}</div>
          <div className="game-info">
            <div className="info-item">
              <div className="info-label">Submitted</div>
              <div className="info-value">{submissionStatus.submittedCount}/{submissionStatus.totalParticipants}</div>
            </div>
          </div>
        </div>
        <div className="game-content">
          {!submitted ? (
            <div style={{ maxWidth: '600px', margin: '0 auto' }}>
              <h3 style={{ marginBottom: '1.5rem', textAlign: 'center' }}>Answer Your Questions</h3>
              {selectedQuestions.map((question, idx) => (
                <div key={idx} style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                    {idx + 1}. {question}
                  </label>
                  <textarea
                    value={answers[question] || ''}
                    onChange={(e) => handleAnswerChange(question, e.target.value)}
                    style={{
                      width: '100%',
                      minHeight: '80px',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: '2px solid var(--secondary)',
                      fontSize: '1rem',
                      resize: 'vertical'
                    }}
                    placeholder="Type your answer here..."
                  />
                </div>
              ))}
              <button
                onClick={handleSubmit}
                style={{
                  width: '100%',
                  padding: '1rem',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  backgroundColor: 'var(--primary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  marginTop: '1rem'
                }}
              >
                Submit Answers
              </button>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <h3 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>✅ Answers Submitted!</h3>
              <p style={{ fontSize: '1.1rem', marginBottom: '2rem' }}>
                {submissionStatus.submittedCount}/{submissionStatus.totalParticipants} players have submitted
              </p>
              {isAdmin && (
                <button
                  onClick={handleEndGame}
                  style={{
                    padding: '1rem 2rem',
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    backgroundColor: 'var(--error)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  End Game & View Results
                </button>
              )}
            </div>
          )}
          {isAdmin && !submitted && (
            <div style={{ textAlign: 'center', marginTop: '2rem', paddingTop: '2rem', borderTop: '2px solid var(--secondary)' }}>
              <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                As admin, you can end the game anytime
              </p>
              <button
                onClick={handleEndGame}
                style={{
                  padding: '1rem 2rem',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  backgroundColor: 'var(--error)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                End Game & View Results
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (stage === 'final') {
    if (allAnswers.length === 0) {
      return (
        <div className="game-container">
          <div className="game-header">
            <h2>🤔 Guess The Person</h2>
            <div className="room-id-display">{roomId}</div>
          </div>
          <div className="game-content">
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <p>No submissions yet</p>
            </div>
          </div>
        </div>
      );
    }

    const currentPerson = allAnswers[currentPersonIndex];
    const answerEntries = Object.entries(currentPerson.answers);

    return (
      <div className="game-container">
        <div className="game-header">
          <h2>🤔 Guess The Person</h2>
          <div className="room-id-display">{roomId}</div>
          <div className="game-info">
            <div className="info-item">
              <div className="info-label">Person</div>
              <div className="info-value">{currentPersonIndex + 1}/{allAnswers.length}</div>
            </div>
            <div className="info-item">
              <div className="info-label">Correct</div>
              <div className="info-value" style={{ color: 'green' }}>{correctCount}</div>
            </div>
            <div className="info-item">
              <div className="info-label">Incorrect</div>
              <div className="info-value" style={{ color: 'red' }}>{incorrectCount}</div>
            </div>
          </div>
        </div>
        <div className="game-content">
          <div style={{ maxWidth: '700px', margin: '0 auto' }}>
            {revealed && (
              <div style={{
                textAlign: 'center',
                padding: '1.5rem',
                backgroundColor: 'var(--primary)',
                color: 'white',
                borderRadius: '8px',
                marginBottom: '2rem',
                fontSize: '1.5rem',
                fontWeight: '700'
              }}>
                👤 {currentPerson.userAlias}
              </div>
            )}
            
            <div style={{ marginBottom: '2rem' }}>
              {answerEntries.map(([question, answer], idx) => (
                <div key={idx} style={{
                  marginBottom: '1.5rem',
                  padding: '1rem',
                  backgroundColor: 'var(--secondary)',
                  borderRadius: '8px'
                }}>
                  <div style={{ fontWeight: '600', marginBottom: '0.5rem', color: 'var(--primary)' }}>
                    {question}
                  </div>
                  <div style={{ fontSize: '1.1rem' }}>{answer}</div>
                </div>
              ))}
            </div>

            {!revealed ? (
              <button
                onClick={handleReveal}
                style={{
                  width: '100%',
                  padding: '1rem',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  backgroundColor: 'var(--primary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Reveal This Person
              </button>
            ) : (
              <div>
                <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                  <button
                    onClick={() => handleGuessResult(true)}
                    style={{
                      flex: 1,
                      padding: '1rem',
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      backgroundColor: 'green',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    ✓ Guessed Correctly
                  </button>
                  <button
                    onClick={() => handleGuessResult(false)}
                    style={{
                      flex: 1,
                      padding: '1rem',
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      backgroundColor: 'red',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    ✗ Guessed Incorrectly
                  </button>
                </div>
                {currentPersonIndex < allAnswers.length - 1 && (
                  <button
                    onClick={handleNextPerson}
                    style={{
                      width: '100%',
                      padding: '1rem',
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      backgroundColor: 'var(--primary)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    Next Person →
                  </button>
                )}
                {currentPersonIndex === allAnswers.length - 1 && (
                  <div style={{
                    textAlign: 'center',
                    padding: '1rem',
                    fontSize: '1.2rem',
                    fontWeight: '600',
                    color: 'var(--primary)'
                  }}>
                    🎉 Game Complete!
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export default GuessThePerson;
